import {
    Anchor,
    Breadcrumbs,
    Group,
    Stack,
} from '@mantine/core'
import IonIcon from '@reacticons/ionicons'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import SegmentControlUpdated from 'app/views/components/segments/segment-control-updated'
import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
    getFacilities,
    getFacility,
} from 'app/state/ducks/facilities/selectors'
import {
    setOneFacility,
} from 'app/state/ducks/facilities/thunks'
import { FacilityParams } from 'app/models/facility-params'
import { getFacilityAmendments } from 'app/state/ducks/facility-amendments/selectors'
import FacilityHeader from '../facility-details/facility-header'
import FacilityActions from '../facility-details/facility-actions'
import { getDeals } from 'app/state/ducks/deals/selectors'
import { CustomLoaderPage } from 'app/views/components/loader/loader-page'
import { getFacilityLendersPositionsWithLoading } from 'app/state/ducks/lenders-positions/selectors'


interface props {
    loading?: boolean
}

export function FacilityDrillPage({ loading }: props) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { facilityId, status } = useParams()
    const facility = useSelector(getFacility)
    const deals = useSelector(getDeals)
    const deal = deals.find(deal => deal.id === facility?.dealId?.id)
    const allFacilities: FacilityParams[] = useSelector(getFacilities, shallowEqual)
    const facilityAmendments: FacilityParams[] = useSelector(
        getFacilityAmendments, shallowEqual
    )
    const facilityLenderPositions = useSelector(getFacilityLendersPositionsWithLoading)

    const approvedFacility = allFacilities.find(facts => facility?.id === facts.id && facts.status === 'Approved');

    const facilityRowObject = approvedFacility && !facilityLenderPositions.isLoading
        ? {
            ...facility,
            approvedFacility: {
                ...approvedFacility,
                lendersPosition: facilityLenderPositions.data ?? [],
            },
        } as FacilityParams
        : facility


    useEffect(() => {
        if (!facilityId) return;

        let updatedFacility: FacilityParams | undefined;

        // Prefer amended facility if available
        if (facilityAmendments?.length) {
            const amended = facilityAmendments.find(
                amendment => amendment.id === facilityId
            );
            if (amended) {
                updatedFacility = { ...amended, amendment: true };
            }
        }

        // Otherwise, use the facility from allFacilities
        if (!updatedFacility && allFacilities?.length) {
            const selected = allFacilities.find(
                facility => facility.id === facilityId
            );
            if (selected) {
                updatedFacility = { ...selected, amendment: false };
            }
        }

        if (updatedFacility) {
            dispatch(setOneFacility(updatedFacility));
        }
    }, [facilityId, facilityAmendments, allFacilities, dispatch]);


    const tabs = {
        facilityDetails: { route: `/dealmanagement/facilities/${facilityId}/${status}`, label: 'OVERVIEW' },
        facilityHistory: { route: `/dealmanagement/facilities/${facilityId}/${status}/history`, label: 'HISTORY' },
        facilityDetailsLoans: { route: `/dealmanagement/facilities/${facilityId}/${status}/loans`, label: 'LOANS' },
        facilityDetailsLoCs: { route: `/dealmanagement/facilities/${facilityId}/${status}/locs`, label: 'LETTERS OF CREDIT' },
        facilityNotes: { route: `/dealmanagement/facilities/${facilityId}/${status}/notes`, label: 'NOTES' },
        facilityAttachments: { route: `/dealmanagement/facilities/${facilityId}/${status}/attachments`, label: 'ATTACHMENTS' },
    }

    const items = [
        { title: 'Deal Management', href: '/dealmanagement' },
        { title: deal?.dealName ?? 'Deal', href: `/dealmanagement/deal/${deal?.id}/${deal?.status}` },
    ].map((item, index) => (
        <Anchor
            onClick={() => navigate(item.href)}
            key={index}
            color="#374151"
            fw={500}
            fz="14px"
        >
            {item.title}
        </Anchor>
    ))

    return (
        <Stack w="100%">
            <Breadcrumbs
                separator={<IonIcon name="chevron-forward-outline" />}
            >
                {items}
            </Breadcrumbs>
            {facility ? <FacilityHeader facility={facility} showStatusPill={true} /> : null}
            <Group w="100%" position="apart" className='tabsGapsActivity'>
                <Group position="right">
                    <SegmentControlUpdated tabs={tabs} loading={loading} />
                </Group>
                <Group noWrap>
                    {facility && <FacilityActions primaryInput={true} row={facilityRowObject ?? facility} withRedirection extended loading={loading} />}
                </Group>
            </Group>
            {loading ? <CustomLoaderPage /> :

                <Group noWrap w='100%' mt='10px'>
                    <Outlet />
                </Group>
            }
        </Stack>
    )
}